import { useEffect, useState } from 'react';
import './my-team.styles.scss';
import { lineupChanges } from '@/utils/lineupChanges';
import { postseasonStatus } from '@/utils/postseasonStatus';
import { gameDayText, midSentence, nextGamesText } from '@/utils/nextGames';
import { Link, useNavigate } from 'react-router-dom';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { inOffseason, offseasonNext, phaseLabel, recordText } from '@/utils/leagueLabels';
import { levels } from '@/utils/levels';
import { portalVisible } from '@shared/portal.js';
import { draftQueueOpen } from '@shared/draft.js';
import { paybackReason, rivalryOf } from '@shared/rivalry.js';
import { openScholarships, recruitingOpen } from '@shared/recruiting.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import { recruitNews } from '@/utils/recruitNews';
import { contactsLeft, useScouting } from '@/utils/useScouting';
import { positions } from '@/utils/positions';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamName from '@/components/SHARED/team-name/team-name.component';
import RankChart from '@/components/SHARED/rank-chart/rank-chart.component';
import TeamHeader from '@/components/SHARED/team-header/team-header.component';
import TeamLeaders from '@/components/HOME/team-leaders/team-leaders.component';
import TeamSchedule from '@/components/SHARED/team-schedule/team-schedule.component';
import TeamHistory from '@/components/SHARED/team-history/team-history.component';
import TeamRivals from '@/components/SHARED/team-rivals/team-rivals.component';
import RecruitClass from '@/components/SHARED/recruit-class/recruit-class.component';
import ContactsLeft from '@/components/SHARED/contacts-left/contacts-left.component';
import NewsHeadlines from '@/components/SHARED/news-view/news-headlines.component';
import Modal, { ModalAlert } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

// The manager's team page: next game, injuries, recent results, team leaders.
const MyTeam = ({ league, teams, schedule, team, teamLink, gameLink, playerLink, seasonLink, postseason, rankings, rankHistory, askResults }) => {

    const [ confirmLeave, setConfirmLeave ] = useState(false);
    const [ leaving, setLeaving ] = useState(false);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const [ tradeOffers, setTradeOffers ] = useState(0);
    const [ recruits, setRecruits ] = useState(null);
    const [ savingAsk, setSavingAsk ] = useState(false);
    const navigate = useNavigate();
    const pro = league.type === 'pro';

    // The recruiting class, for news of commitments (college).
    useEffect(() => {
        if (pro) return;
        let unsub;
        listenToDoc({ 'path': `leagues/${league.id}/summary/recruits`, 'callback': setRecruits, 'handleListener': (u) => { unsub = u; } });
        return () => { if (unsub) unsub(); };
    }, [league.id, pro])
    const news = pro ? [] : recruitNews(recruits, league, team.id, teams);
    // Today's recruiting contacts (college, while recruiting is open).
    const recruitingNow = !pro && recruitingOpen(league);
    const scouting = useScouting(league.id, recruitingNow ? team.id : null);

    // Trade offers waiting for this team's answer (pro).
    useEffect(() => {
        if (!pro) return;
        let unsub;
        listenToDoc({
            'path': `leagues/${league.id}/summary/trades`,
            'callback': (d) => setTradeOffers(Object.values(d?.offers ?? {}).filter(o => o.status === 'pending' && o.toTeamId === team.id).length),
            'handleListener': (u) => { unsub = u; },
        });
        return () => { if (unsub) unsub(); };
    }, [league.id, team.id, pro])

    const conf = league.conferences[team.conference];
    const games = (schedule?.games ?? []).filter(g => g.home === team.id || g.away === team.id);
    const next = games.find(g => !g.result);
    const recent = games.filter(g => g.result).slice(-5).reverse();
    const injured = Object.values(team.players)
        .filter(p => p.injury?.gamesOut > 0)
        .sort((a, b) => b.injury.gamesOut - a.injury.gamesOut);

    const notices = teamNotices(team, recent[0]?.day);
    const postLines = postseasonStatus(league, postseason, team.id);
    const rank = rankings?.teams?.[team.id];
    const offseason = inOffseason(league.season);
    // Who left and who arrived this offseason (the report is labeled with the
    // coming season), shown until the first week of that season is played.
    const reportYear = team.offseason?.year;
    const report = (offseason && reportYear === league.season.year + 1) ||
        (reportYear === league.season.year && league.season.day <= 7) ? team.offseason : null;

    async function leave() {
        setLeaving(true);
        const res = await UseCloudFunction('leaveTeam', { leagueId: league.id });
        setLeaving(false);
        setConfirmLeave(false);
        if (res.error) {
            setAlertMessage(res.error);
            return;
        }
        navigate('/leagues');
    }

    return (
        <div className='manager-league my-team'>
            <TeamHeader
                league={league}
                teams={teams}
                team={team}
                meta={`${conf?.name ?? ''} · ${phaseLabel(league.season, league.type)}`}
                extra={rank?.poll && <span>{league.type === 'pro' ? 'Power ranking' : 'Ranked'} <b>#{rank.poll}</b></span>}
                actions={<Link to='lineup' className='g-button primary'>Set lineup</Link>} />

            {postLines.length > 0 &&
                <div className='g-card postseason-status'>
                    <h3>Postseason</h3>
                    {postLines.map(l => <p key={l.key} className={l.tone}>{l.text}</p>)}
                    <Link to={league.type !== 'pro' && postseason?.selection ? 'national' : 'postseason'} className='notice-link'>See the brackets</Link>
                </div>
            }

            {(offseason || report) &&
                <OffseasonCard league={league} team={team} report={report} offseason={offseason} playerLink={playerLink} />
            }

            {tradeOffers > 0 &&
                <div className='g-card trade-notice' role='status'>
                    <p>You have <b>{tradeOffers}</b> trade offer{tradeOffers === 1 ? '' : 's'} waiting for an answer.</p>
                    <Link to='trades' className='notice-link'>See the offers</Link>
                </div>
            }

            {recruitingOpen(league) &&
                <RecruitClass team={team} title='Recruiting' empty='No commitments yet.'>
                    {scouting !== undefined && <ContactsLeft league={league} left={contactsLeft(scouting, league)} />}
                    {news.length > 0 &&
                        <ul className='recruit-news'>{news.map(n => <li key={n.key} className={n.good ? 'good' : 'bad'}>{n.text}</li>)}</ul>}
                    <p className='meta'>
                        {openScholarships(team, league.settings.rosterSize)} scholarship{openScholarships(team, league.settings.rosterSize) === 1 ? '' : 's'} open for next season · signing day is day {league.season.totalDays}
                    </p>
                    <Link to='recruiting' className='notice-link'>Go to recruiting</Link>
                </RecruitClass>
            }

            {notices.length > 0 &&
                <div className='g-card team-notices' role='status'>
                    {notices.map(n => <p key={n.key}>{n.text}</p>)}
                    {notices.some(n => n.lineup) && <Link to='lineup' className='notice-link'>Review your lineup</Link>}
                </div>
            }

            <div className='my-team-grid'>
                <div className='g-card next-game'>
                    <h3>Next game</h3>
                    {next ? <NextGame game={next} team={team} teams={teams} teamLink={teamLink} league={league} schedule={schedule} /> :
                        <p className='meta'>{offseason ? 'The season is over.' : 'No game scheduled yet.'}</p>}
                    {!team.depthSavedAt &&
                        <p className='lineup-hint'>
                            Your team is using the lineup it came with. <Link to='lineup'>Set your own lineup</Link> to choose your starters and rotation.
                        </p>
                    }
                </div>

                <div className='g-card injuries'>
                    <h3>Injuries</h3>
                    {injured.length === 0 ? <p className='meta'>Everyone is healthy.</p> :
                        <ul className='injury-list'>
                            {injured.map(p => (
                                <li key={p.id}>
                                    <span><b>{p.fName} {p.lName}</b> <span className='meta'>{positions[p.pos1 - 1].short}</span></span>
                                    <span className='injury-out'>Out {p.injury.gamesOut} game{p.injury.gamesOut === 1 ? '' : 's'}</span>
                                </li>
                            ))}
                        </ul>
                    }
                </div>
            </div>

            {rankHistory &&
                <div className='g-card'>
                    <RankChart
                        history={rankHistory}
                        teamId={team.id}
                        title={league.type === 'pro' ? 'Power ranking' : 'Ranking'}
                        top25={league.type !== 'pro'}
                        regularDays={league.season.totalDays} />
                </div>
            }

            <NewsHeadlines league={league} teams={teams} teamLink={teamLink} gameLink={gameLink} playerLink={playerLink} highlightId={team.id} newsLink='news' />

            <div className='g-card'>
                <h3>Recent results</h3>
                {recent.length === 0 ? <p className='meta'>No games played yet.</p> :
                    <TeamSchedule teamId={team.id} games={recent} schedule={schedule} year={league.season.year} teams={teams} gameLink={gameLink} teamLink={teamLink} />
                }
                <Link to='schedule' className='see-all'>Full schedule</Link>
            </div>

            <TeamLeaders team={team} playerLink={playerLink} />

            {!recruitingOpen(league) &&
                <RecruitClass team={team}>
                    {news.length > 0 ?
                        <ul className='recruit-news'>{news.map(n => <li key={n.key} className={n.good ? 'good' : 'bad'}>{n.text}</li>)}</ul> : null}
                </RecruitClass>
            }
            <TeamRivals team={team} teams={teams} teamLink={teamLink} />
            <TeamHistory team={team} league={league} seasonLink={seasonLink} />

            <div className='g-card results-setting'>
                <label htmlFor='ask-results' className='ask-row'>
                    <input id='ask-results' type='checkbox' checked={askResults !== false} disabled={savingAsk}
                        onChange={async (e) => {
                            setSavingAsk(true);
                            const res = await UseCloudFunction('setResultsPreference', { ask: e.target.checked });
                            setSavingAsk(false);
                            if (res.error) setAlertMessage(res.error);
                        }} />
                    <span>
                        <b>Ask before showing new results</b>
                        <span className='meta'>When your team has played, choose to watch the game or see the result before anything else gives the score away.</span>
                    </span>
                </label>
            </div>

            <div className='g-card leave-card'>
                <div>
                    <b>Leave this team</b>
                    <p className='meta'>The team will go back to running itself, and another user can take it.</p>
                </div>
                <button className='g-button' onClick={() => setConfirmLeave(true)}>Leave team</button>
            </div>

            <Modal show={confirmLeave} cancel={() => setConfirmLeave(false)} text='Leave this team?' closeButton={true} cls='narrow'>
                <div className='modal-children leave-confirm'>
                    <p>You will stop managing {team.name}. Its record and players stay with the team.</p>
                    <div className='buttons'>
                        <button className='g-button' onClick={() => setConfirmLeave(false)}>Cancel</button>
                        <button className={`g-button primary ${leaving ? 'disabled' : ''}`} disabled={leaving} onClick={leave}>
                            {leaving ? <ThreeDotsLoader /> : 'Leave team'}
                        </button>
                    </div>
                </div>
            </Modal>
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

// Things the manager should know before the next game: players hurt in the
// latest game, and starting spots that change because of injuries (worked
// out with the sim's own rule, startingLineup).
function teamNotices(team, lastDay) {
    const notices = [];
    const out = (p) => p?.injury?.gamesOut > 0;
    const name = (p) => `${p.fName} ${p.lName}`;
    const games = (n) => `${n} game${n === 1 ? '' : 's'}`;
    for (const p of Object.values(team.players)) {
        if (out(p) && lastDay && p.injury.day === lastDay) {
            notices.push({ key: `hurt-${p.id}`, text: `${name(p)} was hurt on day ${lastDay} and will miss ${games(p.injury.gamesOut)}.` });
        }
    }
    for (const c of lineupChanges(team.players, team.depth)) {
        notices.push({ key: `start-${c.posId}`, lineup: true, text: c.text });
    }
    return notices;
}

const LEFT = { graduated: 'graduated', retired: 'retired', transferred: 'entered the portal', cut: 'cut', 'free agent': 'became a free agent' };

// The offseason: what happens next and when, then (after development) who
// graduated or retired, who arrived and how the returning players changed.
const OffseasonCard = ({ league, team, report, offseason, playerLink }) => {
    const when = offseason ? nextGamesText(league) : null;
    const name = (p) => `${p.fName} ${p.lName}`;
    const pos = (p) => positions[p.pos1 - 1]?.short;
    const klass = (p) => (p.class ? levels[p.class - 1]?.short : p.age ? `age ${p.age}` : '');
    // Awards your players won in the season just finished.
    const lastYear = offseason ? league.season.year : league.season.year - 1;
    const honors = Object.values(team.players).flatMap(p =>
        (p.career?.find(c => c.year === lastYear)?.awards ?? []).map(award => ({ p, award })));
    const changes = Object.entries(report?.changes ?? {})
        .map(([id, delta]) => ({ p: team.players[id], delta }))
        .filter(c => c.p)
        .sort((a, b) => b.delta - a.delta);
    return (
        <div className='g-card offseason-card'>
            <h3>{offseason ? 'Offseason' : `Season ${league.season.year} roster changes`}</h3>
            {offseason &&
                <p>{offseasonNext(league)}{when ? <> Next step <b>{midSentence(when)}</b>.</> : ''}</p>
            }
            {honors.length > 0 &&
                <div className='offseason-honors'>
                    <h4>Awards</h4>
                    <ul>{honors.map(({ p, award }) => (
                        <li key={`${p.id}-${award}`}><Link to={playerLink(team.id, p.id)}>{name(p)}</Link>: <b>{award}</b></li>
                    ))}</ul>
                </div>
            }
            {report &&
                <div className='offseason-moves'>
                    <div>
                        <h4>Left the team</h4>
                        {report.departed.length === 0 ? <p className='meta'>Nobody.</p> :
                            <ul>{report.departed.map(p => <li key={p.id}>{name(p)} <span className='meta'>{pos(p)} · {p.overall} · {LEFT[p.reason] ?? p.reason}</span></li>)}</ul>}
                    </div>
                    <div>
                        <h4>New players</h4>
                        {report.joined.length === 0 ? <p className='meta'>Nobody yet.</p> :
                            <ul>{report.joined.map(p => (
                                <li key={p.id}>
                                    {team.players[p.id] ? <Link to={playerLink(team.id, p.id)}>{name(p)}</Link> : name(p)} <span className='meta'>{pos(p)} · {klass(p)} · {p.overall}{joinedText(p, league)}</span>
                                </li>
                            ))}</ul>}
                    </div>
                    {changes.length > 0 &&
                        <div>
                            <h4>Returning players</h4>
                            <ul>{changes.map(({ p, delta }) => (
                                <li key={p.id}>
                                    <Link to={playerLink(team.id, p.id)}>{name(p)}</Link> <span className='meta'>{pos(p)} · {p.overall}</span>{' '}
                                    <span className={delta > 0 ? 'up' : delta < 0 ? 'down' : 'meta'}>{delta > 0 ? `+${delta}` : delta === 0 ? '±0' : delta}</span>
                                </li>
                            ))}</ul>
                        </div>
                    }
                </div>
            }
            {portalVisible(league) && <Link to='portal' className='notice-link portal-link'>Go to the transfer portal</Link>}
            {draftQueueOpen(league) && <Link to='draft' className='notice-link portal-link'>Go to the draft</Link>}
            {league.type === 'pro' && offseason && <Link to='office' className='notice-link portal-link'>Go to the front office</Link>}
            {report && <Link to='lineup' className='notice-link'>Check your lineup</Link>}
        </div>
    )
}

// How a new player got here: a recruit or a transfer (college), a draft
// pick or a free agent signing (pro).
function joinedText(p, league) {
    if (p.recruit) return p.stars ? ` · ${p.stars}★ recruit` : ' · recruit';
    if (!p.from) return '';
    if (league.type !== 'pro') return ` · transfer from ${p.from}`;
    const pick = /^draft #(\d+)$/.exec(p.from)?.[1];
    return pick ? ` · draft pick #${pick}` : ` · signed (${p.from})`;
}

const NextGame = ({ game, team, teams, teamLink, league, schedule }) => {
    const home = game.home === team.id;
    // When it's played, if the league's daily games are on.
    const tipoff = gameDayText(league, game.day);
    const oppId = home ? game.away : game.home;
    const opp = teams?.[oppId];
    const rivalry = rivalryOf(team, oppId);
    const payback = paybackReason({ teamId: team.id, oppId, games: schedule?.games, day: game.day, payback: team.payback, year: league.season.year });
    const lostBy = (g) => {
        const r = g.result;
        const mine = g.home === team.id ? r.home : r.away;
        const theirs = g.home === team.id ? r.away : r.home;
        return `lost ${theirs}-${mine} on day ${g.day}`;
    };
    return (
        <div className='next-game-row'>
            <TeamBadge team={opp} size='md' />
            <div className='next-game-text'>
                <div className='meta'>Day {game.day} · {game.neutral ? 'Neutral site' : home ? 'Home' : 'Away'}{game.conf ? ' · Conference' : ''}</div>
                {game.label && <div className='meta next-label'>{game.label}</div>}
                <div className='next-opp'>
                    {home ? 'vs' : '@'} <Link to={teamLink(oppId)}><TeamName team={opp} id={oppId} /></Link>
                </div>
                {opp &&
                    <div className='meta'>{recordText(opp.record)} · Chemistry {opp.chemistryGrade?.grade ?? '-'}</div>
                }
                {rivalry &&
                    <div className='next-rivalry'>{rivalry.name ?? 'Rivalry game'} <span className='meta'>· series {rivalry.w ?? 0}-{rivalry.l ?? 0}</span></div>
                }
                {payback &&
                    <div className='next-payback'>Payback game: {payback.game ? lostBy(payback.game) : 'they ended your last season'}</div>
                }
                {tipoff && <div className='next-tipoff'>Tips off <b>{midSentence(tipoff)}</b></div>}
                <div className='next-plan-links'>
                    <Link to={`preview/${game.id}`}>Matchup preview</Link>
                    <Link to={`teams/${oppId}/scout`}>Scouting report</Link>
                    <Link to={`strategy?game=${game.id}#game-plans`}>Game plan</Link>
                </div>
            </div>
        </div>
    )
}

export default MyTeam
